import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Typography } from '@mui/material';
import { EBalanceType, ITransaction, TransactionTypeTranslations } from 'common/transactions.interface';
import { formatNumberToBRL } from 'utils/formatters';
import PaymentStatusPill from '../../PaymentsPage/components/PaymentStatusPill';
import Chip from '../../ReceivePage/components/Chip';

interface SummaryTableProps {
  transactions: ITransaction[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export const SummaryTable: FC<SummaryTableProps> = ({ transactions, loading, hasMore, onLoadMore }) => {
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    });

    const target = observerTarget.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleObserver]);

  const checkIfDebit = (transaction: ITransaction) => transaction.balanceType === EBalanceType.DEBIT;

  return (
    <Box sx={{ mt: '24px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#4E4D4D', fontSize: '22px', fontWeight: 600 }}>
          Minhas transações do dia
        </Typography>
      </Box>

      <TableContainer sx={{ mt: '1vh' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F7F6F6' }}>
              <TableCell align="left">
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Contraparte
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Transação
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Valor
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Status
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id} sx={{ borderBottom: '1px solid #E9E8E7' }} hover>
                <TableCell align="left">
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    {transaction.counterpartyName}
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography variant="body2">{TransactionTypeTranslations[transaction.type]}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ color: '#656464', fontWeight: 600 }}>
                    {checkIfDebit(transaction) ? '-' : ''}
                    {formatNumberToBRL(transaction.value)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {transaction.balanceType === EBalanceType.CREDIT ? (
                    <Chip transaction={transaction} />
                  ) : (
                    <PaymentStatusPill status={transaction.status} />
                  )}
                </TableCell>
              </TableRow>
            ))}

            {!loading && transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhuma transação encontrada para o período selecionado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && hasMore && transactions.length > 0 && <Box ref={observerTarget} sx={{ height: '20px' }} />}
    </Box>
  );
};
